import { useEffect, FC } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import find from 'lodash/find';
import { Trans } from 'react-i18next';
import MaintenanceImage from './images_pp-people-maintenance.png';
import { PanelInfo, Link as LinkType } from '../../survey-tool/declarations/ah-api';
import { useAppSelector } from '../../store';
import { browserNavigate } from '../../utils/browser-navigate';
import { ensureProtocol } from '../../utils/uri';
import { PPButton } from '../../survey-tool/components/pp-button';
import { TermsFooter } from '../footer/terms';
import { useAppTranslation } from '../../i18n';

const styles = {
  container: {
    flexGrow: 1,
    background: '#ffffff',
    textAlign: 'center',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: '100%',
    maxWidth: '345px',
    marginBottom: '30px',
  },
  titleFn: (p: PanelInfo | null) => ({
    lineHeight: '49px',
    color: p?.app_theme.color_secondary ?? '#000000',
    fontFamily: 'RooneySans-Bold, sans-serif',
    fontSize: '44px',
    fontWeight: 'bold',
    textAlign: 'center',
    margin: '0 0 30px',
  }),
  subtitle: {
    color: '#484A4D',
    fontFamily: '"Open Sans", sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    marginBottom: '20px',
  },
} as const;

const Maintenance: FC = () => {
  const panelInfo = useAppSelector((state) => state.panelInfo);
  const contactUs = find(panelInfo?.app_settings.links, (m: LinkType) => m.type === 'contactus');
  const secondaryLogoImageUrl = panelInfo?.app_theme.secondary_logo_image_url;
  const { t } = useAppTranslation();

  useEffect(() => {
    // if set, redirect to this value
    if (panelInfo?.maintenance_page_url && panelInfo.maintenance_page_url !== window.location.href) {
      browserNavigate(ensureProtocol(panelInfo.maintenance_page_url));
    }
  }, [panelInfo?.maintenance_page_url]);

  useEffect(() => {
    // makes sure page isn't kept open for too long
    const timeoutId = setTimeout(
      () => {
        browserNavigate('/');
      },
      5 * 60 * 1000 /* 5 minutes */
    );
    return () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" sx={styles.container}>
      <Grid item xs={10} md={4} sx={styles.main}>
        <img alt="" src={MaintenanceImage} style={styles.image} />
        <Typography variant="h3" sx={styles.titleFn(panelInfo)}>
          {t('weWillBeBackSoon', "We'll be back soon")}
        </Typography>
        <Typography variant="h5" sx={styles.subtitle}>
          {t(
            'sorryForTheInconvenience',
            "Sorry for the inconvenience, we're currently performing scheduled updates and maintenance to improve your experience."
          )}
        </Typography>
        {contactUs && (
          <Typography sx={styles.subtitle}>
            <Trans t={t} i18nKey="backShortlyHoweverIfYouNeedUsPleaseContactSupport">
              We'll be back shortly however if you need us please &nbsp;
              <a href={contactUs.url} style={{ textDecoration: 'underline' }}>
                contact support
              </a>
              &nbsp; in the meantime.
            </Trans>
          </Typography>
        )}
        <PPButton onClick={() => browserNavigate('/')} style={{ marginTop: 20 }}>
          {t('goBackToTheApp', 'Go back to the app')}
        </PPButton>
        {secondaryLogoImageUrl && (
          <img
            src={secondaryLogoImageUrl}
            alt=""
            width={80}
            x-name="maintenance-logo"
            className="logo"
            style={{ marginTop: 50 }}
          />
        )}
      </Grid>
      <TermsFooter />
    </Grid>
  );
};

export default Maintenance;
